import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import * as Sentry from '@sentry/react';
import './App.css';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ChildProvider } from './contexts/ChildContext';
import LandingPage from './pages/LandingPage';
import DocsPage from './pages/DocsPage';
import RoadmapPage from './pages/RoadmapPage';
import PrivacyPolicyPage from './pages/PrivacyPolicyPage';
import TermsOfServicePage from './pages/TermsOfServicePage';
import InviteAcceptPage from './pages/InviteAcceptPage';
import PublicJournalPage from './pages/PublicJournalPage';
import OfflineBanner from './components/ui/OfflineBanner';

// Layout
import AppLayout from './components/layout/AppLayout';

// Page components
import DashboardPage from './pages/DashboardPage';
import BottleFeedsPage from './pages/BottleFeedsPage';
import BreastFeedingPage from './pages/BreastFeedingPage';
import SolidFoodsPage from './pages/SolidFoodsPage';
import FoodIntroductionsPage from './pages/FoodIntroductionsPage';
import DiapersPage from './pages/DiapersPage';
import SleepPage from './pages/SleepPage';
import WaterIntakePage from './pages/WaterIntakePage';
import GrowthHealthPage from './pages/GrowthHealthPage';
import MilestonesPage from './pages/MilestonesPage';
import DoctorVisitsPage from './pages/DoctorVisitsPage';
import VaccinationsPage from './pages/VaccinationsPage';
import AllergiesPage from './pages/AllergiesPage';
import HealthRecordsPage from './pages/HealthRecordsPage';
import JournalPage from './pages/JournalPage';
import TimelinePage from './pages/TimelinePage';
import SettingsPage from './pages/SettingsPage';
import AdminLayout from './components/layout/AdminLayout';
import AdminFeedbackPage from './pages/AdminFeedbackPage';

/**
 * PrivateRoute — redirects unauthenticated users to the landing page.
 */
function PrivateRoute({ children }) {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center min-vh-100">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading…</span>
        </div>
      </div>
    );
  }

  return user ? children : <Navigate to="/" replace />;
}

/**
 * PublicOnlyRoute — redirects authenticated users straight to /app.
 * Used for the landing page and docs so logged-in users aren't stuck there.
 */
function PublicOnlyRoute({ children }) {
  const { user, loading } = useAuth();

  if (loading) return null; // avoid flash

  return user ? <Navigate to="/app" replace /> : children;
}

/**
 * AppRoutes — defined inside AuthProvider so hooks can access auth state.
 */
function AppRoutes() {
  return (
    <div className="App">
      <OfflineBanner />
      <SentryTestButton />
      <Routes>
        {/* Public-only: redirect to /app if already logged in */}
        <Route path="/" element={
          <PublicOnlyRoute><LandingPage /></PublicOnlyRoute>
        } />
        <Route path="/docs"     element={<DocsPage />} />
        <Route path="/roadmap"  element={<RoadmapPage />} />
        <Route path="/privacy"  element={<PrivacyPolicyPage />} />
        <Route path="/terms"    element={<TermsOfServicePage />} />

        {/* Caregiver invite — readable by anyone */}
        <Route path="/invite/:token" element={<InviteAcceptPage />} />

        {/* Public journal — no auth required */}
        <Route path="/journal/:token" element={<PublicJournalPage />} />

        {/* Authenticated app — all routes under /app use AppLayout */}
        <Route path="/app" element={
          <PrivateRoute>
            <ChildProvider>
              <AppLayout />
            </ChildProvider>
          </PrivateRoute>
        }>
          {/* Overview / dashboard */}
          <Route index element={<DashboardPage />} />

          {/* Tracking sections */}
          <Route path="bottle-feeds" element={<BottleFeedsPage />} />
          <Route path="breast-feeding" element={<BreastFeedingPage />} />
          <Route path="solid-foods" element={<SolidFoodsPage />} />
          <Route path="food-introductions" element={<FoodIntroductionsPage />} />
          <Route path="diapers" element={<DiapersPage />} />
          <Route path="sleep" element={<SleepPage />} />
          <Route path="water-intake" element={<WaterIntakePage />} />
          <Route path="growth-health" element={<GrowthHealthPage />} />
          <Route path="milestones" element={<MilestonesPage />} />
          <Route path="doctor-visits" element={<DoctorVisitsPage />} />
          <Route path="vaccinations" element={<VaccinationsPage />} />
          <Route path="allergies" element={<AllergiesPage />} />
          <Route path="health-records" element={<HealthRecordsPage />} />
          <Route path="journal"   element={<JournalPage />} />
          <Route path="timeline"  element={<TimelinePage />} />
          <Route path="settings"  element={<SettingsPage />} />
        </Route>

        {/* Catch-all */}
        <Route path="*" element={<Navigate to="/" replace />} />

        {/* Admin — AdminLayout handles auth + isAdmin gate */}
        <Route path="/admin" element={
          <PrivateRoute><AdminLayout /></PrivateRoute>
        }>
          <Route path="feedback" element={<AdminFeedbackPage />} />
        </Route>
      </Routes>
    </div>
  );
}

/**
 * Dev-only Sentry test button — throws a real error so you can confirm events
 * are reaching your Sentry dashboard. Only rendered in development; the
 * production build strips it out entirely (NODE_ENV === 'production' is false).
 * Run `npm start`, click the button, then check https://sentry.io.
 */
function SentryTestButton() {
  if (process.env.NODE_ENV !== 'development') return null;
  return (
    <button
      onClick={() => {
        // captureException sends directly to Sentry — bypasses React's event
        // handler error interception which can prevent window.onerror from firing
        Sentry.captureException(new Error('Sentry test — manual verification'));
        alert('Event sent to Sentry — check your Issues dashboard in a few seconds.');
      }}
      style={{
        position: 'fixed',
        bottom: '5rem',
        left: '1rem',
        zIndex: 9999,
        background: '#e53935',
        color: '#fff',
        border: 'none',
        borderRadius: 8,
        padding: '0.4rem 0.75rem',
        fontSize: '0.75rem',
        fontWeight: 700,
        cursor: 'pointer',
        opacity: 0.85,
      }}
      title="Sends a test error directly to Sentry"
    >
      🐛 Test Sentry
    </button>
  );
}

function ErrorFallback({ resetError }) {
  return (
    <div className="d-flex flex-column justify-content-center align-items-center min-vh-100 text-center px-3">
      <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>😕</div>
      <h2 style={{ fontWeight: 700, marginBottom: '0.5rem' }}>Something went wrong</h2>
      <p style={{ color: '#757575', marginBottom: '1.5rem', maxWidth: 360 }}>
        We've been notified and will look into it. Try reloading — your data is safe.
      </p>
      <button
        className="btn btn-primary"
        onClick={resetError}
        style={{ borderRadius: 50 }}
      >
        Reload page
      </button>
    </div>
  );
}

export default function App() {
  return (
    <Sentry.ErrorBoundary fallback={({ resetError }) => <ErrorFallback resetError={resetError} />}>
      <BrowserRouter>
        <AuthProvider>
          <AppRoutes />
        </AuthProvider>
      </BrowserRouter>
    </Sentry.ErrorBoundary>
  );
}
